import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyInfoService } from '@/services/companyInfo';
import type { CompanyInfo } from '@/types';

export function Footer() {
  const navigate = useNavigate();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        setIsLoading(true);
        const result = await companyInfoService.getCompanyInfo();
        
        if (result.success && result.data) {
          setCompanyInfo(result.data);
          setError(null);
        } else {
          setError(result.error || '회사 정보를 불러올 수 없습니다');
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
        setError('회사 정보를 불러오는 중 오류가 발생했습니다');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyInfo();
  }, []);

  const handleTermsClick = () => {
    navigate('/mypage/terms-policies');
  };

  const handlePrivacyClick = () => {
    navigate('/mypage/terms-policies');
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <footer className="bg-[#F8F9FA] border-t border-gray-200" style={{ marginTop: '32px' }}>
        <div style={{ padding: '24px 16px' }}>
          <div className="animate-pulse">
            <div className="space-y-2 mb-6">
              <div className="h-3 bg-gray-300 rounded w-3/4"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
              <div className="h-3 bg-gray-300 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // 에러 상태 또는 데이터가 없는 경우 기본값 표시
  if (error || !companyInfo) {
    return (
      <footer className="bg-[#F8F9FA] border-t border-gray-200" style={{ marginTop: '32px' }}>
        <div style={{ padding: '24px 16px' }}>
          <div className="space-y-2 mb-6">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span className="text-[12px] text-gray-600">
                <span className="font-medium text-gray-700">상호:</span> 에코딜
              </span>
              <span className="text-[12px] text-gray-600">
                <span className="font-medium text-gray-700">대표자:</span> 김기섭
              </span>
            </div>
            
            <div className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">연락처:</span> ecodeal_cs@ecodeal.kr
            </div>
            
            <div className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">사업자등록번호:</span> 330-11-02619
            </div>
            
            <div className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">사업자 주소:</span> 경기 광주시 회안대로 1583번길 52
            </div>
            
            <div className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">호스팅서비스 제공자:</span> Vercel Inc.
            </div>
            
            <div className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">통신판매업신고:</span> -
            </div>
          </div>

          {/* 구분선 */}
          <div className="border-t border-gray-300 mb-4"></div>

          {/* 약관 및 정책 링크 */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleTermsClick}
              className="text-[12px] text-gray-600 underline hover:text-gray-800 transition-colors"
            >
              이용약관
            </button>
            <button
              onClick={handlePrivacyClick}
              className="text-[12px] text-gray-600 underline hover:text-gray-800 transition-colors"
            >
              개인정보 처리방침
            </button>
          </div>

          {/* Copyright */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <p className="text-[11px] text-gray-500">
              © 2025 에코딜. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    );
  }

  // 정상 데이터 표시
  return (
    <footer className="bg-[#F8F9FA] border-t border-gray-200" style={{ marginTop: '32px' }}>
      <div style={{ padding: '24px 16px' }}>
        
        {/* 사업자 정보 */}
        <div className="space-y-2 mb-6">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">상호:</span> {companyInfo.company_name}
            </span>
            <span className="text-[12px] text-gray-600">
              <span className="font-medium text-gray-700">대표자:</span> {companyInfo.ceo_name}
            </span>
          </div>
          
          <div className="text-[12px] text-gray-600">
            <span className="font-medium text-gray-700">연락처:</span> {companyInfo.contact_email}
          </div>
          
          <div className="text-[12px] text-gray-600">
            <span className="font-medium text-gray-700">사업자등록번호:</span> {companyInfo.business_registration_number}
          </div>
          
          <div className="text-[12px] text-gray-600">
            <span className="font-medium text-gray-700">사업자 주소:</span> {companyInfo.business_address}
          </div>
          
          <div className="text-[12px] text-gray-600">
            <span className="font-medium text-gray-700">호스팅서비스 제공자:</span> {companyInfo.hosting_provider}
          </div>
          
          <div className="text-[12px] text-gray-600">
            <span className="font-medium text-gray-700">통신판매업신고:</span> {companyInfo.telecom_sales_report_number || '-'}
          </div>
        </div>

        {/* 구분선 */}
        <div className="border-t border-gray-300 mb-4"></div>

        {/* 약관 및 정책 링크 */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleTermsClick}
            className="text-[12px] text-gray-600 underline hover:text-gray-800 transition-colors"
          >
            이용약관
          </button>
          <button
            onClick={handlePrivacyClick}
            className="text-[12px] text-gray-600 underline hover:text-gray-800 transition-colors"
          >
            개인정보 처리방침
          </button>
        </div>

        {/* Copyright */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-[11px] text-gray-500">
            © 2025 {companyInfo.company_name}. All rights reserved.
          </p>
        </div>

      </div>
    </footer>
  );
}